import { useMemo, useState } from "react";
import { PokemonSprite } from "./pokemon-sprite";
import { Pokemon } from "prisma-layer";
import { Direction } from "types/battle-engine/battle";
import classNames from "classnames";
import { LightSource } from "@/components/environment/lighting-effects";
import { isXsScreen } from "@/utils/screen-size-check";
import { Intent, useSPAContext } from "@/providers/spa-provider";
import { RouteNames } from "@/types/route-names";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { AddIcon } from "@/icons";

interface PokemonCampfireDisplayProps {
  pokemons: Array<any>;
  className?: string;
  onPokemonClick?: (pokemon: any) => void;
}

export function PokemonCampfireDisplay({
  pokemons,
  className,
  onPokemonClick,
}: PokemonCampfireDisplayProps) {
  const { setSelectedPokemons, setCurrentRoute, setPreviousRoute, setIntent } =
    useSPAContext();
  const [hoveredId, setHoveredId] = useState<number | string | null>(null);

  const sleepingPokemonIds = useMemo(() => {
    const count = pokemons?.length ?? 0;
    if (count === 0) return new Set<number>();
    const r = Math.random();
    let numToSleep = 0;

    if (r < 0.5) numToSleep = 1;
    else if (r < 0.7) numToSleep = 2;

    numToSleep = Math.min(numToSleep, count);

    const indices = new Set<number>();
    while (indices.size < numToSleep) {
      indices.add(Math.floor(Math.random() * count));
    }

    return new Set(
      Array.from(indices)
        .map((i) => pokemons[i]?.id)
        .filter(Boolean),
    );
  }, [pokemons]);

  // 8 positions distributed around a circle/ellipse
  // We place them relative to the center
  const getPositionAndDirection = (index: number) => {
    // Radius for the ellipse
    const radiusX = isXsScreen() ? 145 : 166; // Horizontal spread
    const radiusY = isXsScreen() ? 121 : 141; // Vertical spread

    // We define explicit positions for the 8 slots starting from bottom and going clockwise (or counter-clockwise)
    // We want them to face the center (0,0)

    // Position 0: Bottom (South). Faces Back (North).
    // Position 1: Bottom-Left. Faces Back-Right (North-East).
    // Position 2: Left (West). Faces Right (East).
    // Position 3: Top-Left. Faces Front-Right (South-East).
    // Position 4: Top (North). Faces Front (South).
    // Position 5: Top-Right. Faces Front-Left (South-West).
    // Position 6: Right (East). Faces Left (West).
    // Position 7: Bottom-Right. Faces Back-Left (North-West).

    const positions = [
      // Angle is the position angle on the circle (0 is right, 90 is bottom)
      { angle: 90, dir: Direction.N }, // Bottom
      { angle: 135, dir: Direction.NE }, // Bottom-Left
      { angle: 180, dir: Direction.E }, // Left
      { angle: 225, dir: Direction.SE }, // Top-Left
      { angle: 270, dir: Direction.S }, // Top
      { angle: 315, dir: Direction.SW }, // Top-Right
      { angle: 0, dir: Direction.W }, // Right
      { angle: 45, dir: Direction.NW }, // Bottom-Right
    ];

    const pos = positions[index % 8]!;
    const rad = (pos.angle * Math.PI) / 180;

    const x = Math.cos(rad) * radiusX;
    const y = Math.sin(rad) * radiusY;

    // Higher Y (lower on screen) means closer to viewer, so higher Z-index
    // We add base z-index to ensure they are above background
    const zIndex = Math.floor(y + 200);

    return { x, y, direction: pos.dir, zIndex };
  };

  const handlePokemonClick = (pokemon: any) => {
    if (onPokemonClick) {
      onPokemonClick(pokemon);
      return;
    }
    setSelectedPokemons([pokemon]);
    setPreviousRoute(undefined);
    setCurrentRoute(RouteNames.POKEMON_INFO);
  };

  const handleAddClick = () => {
    setIntent(Intent.SELECTING_POKEMON_FOR_MAIN_DECK);
    setCurrentRoute(RouteNames.INVENTORY_POKE);
  };

  return (
    <div
      className={classNames(
        "relative w-full h-64 flex items-center justify-center animate-fadeIn",
        className,
      )}
    >
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <LightSource
          size={350}
          intensity={0.8}
          flicker={true}
          pulse={true}
          pulseSpeed={3}
        />
      </div>
      {Array.from({ length: 8 }).map((_, index) => {
        const pokemon = pokemons?.[index];
        const { x, y, direction, zIndex } = getPositionAndDirection(index);
        const finalY = isXsScreen() ? y - 17 : y - 60;

        if (!pokemon) {
          const isHovered = hoveredId === `empty-${index}`;
          return (
            <div
              key={`empty-${index}`}
              className="absolute flex flex-col items-center justify-center transition-all duration-500 cursor-pointer group"
              onMouseEnter={() => setHoveredId(`empty-${index}`)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={handleAddClick}
              style={{
                transform: `translate(${x}px, ${finalY}px)`,
                zIndex: isHovered ? 1000 : zIndex,
              }}
            >
              <div
                className={classNames(
                  "w-12 h-12 rounded-full border-2 border-dashed border-pk-gray-3 flex items-center justify-center bg-black/20 transition-all duration-200 group-hover:scale-110 group-hover:border-pk-orange-4 group-hover:bg-pk-orange-4/10",
                  {
                    "scale-110 border-pk-orange-4 bg-pk-orange-4/10": isHovered,
                  },
                )}
              >
                <AddIcon
                  width={24}
                  height={24}
                  fill={isHovered ? "#ff9800" : "#9e9e9e"}
                />
              </div>
              <div
                className={classNames(
                  "absolute -bottom-6 opacity-0 transition-opacity duration-200 whitespace-nowrap",
                  { "opacity-100": isHovered },
                )}
              >
                <StrokedText fontSize={12} strokeWidth={4}>
                  Add Pokémon
                </StrokedText>
              </div>
            </div>
          );
        }

        const isHovered = hoveredId === pokemon.id;

        return (
          <div
            key={pokemon.id}
            className="absolute flex flex-col items-center justify-center transition-all duration-500 cursor-pointer group"
            onMouseEnter={() => setHoveredId(pokemon.id)}
            onMouseLeave={() => setHoveredId(null)}
            onClick={() => handlePokemonClick(pokemon)}
            style={{
              transform: `translate(${x}px, ${finalY}px)`,
              zIndex: isHovered ? 1000 : zIndex,
            }}
          >
            {/* Hover Info */}
            <div
              className={classNames(
                "absolute -top-16 flex flex-col items-center transition-all duration-300 pointer-events-none",
                {
                  "opacity-100 translate-y-0": isHovered,
                  "opacity-0 translate-y-2": !isHovered,
                },
              )}
            >
              <StrokedText
                fontSize={isXsScreen() ? 14 : 18}
                strokeWidth={6}
                className="whitespace-nowrap"
              >
                {pokemon.name || pokemon.base_pokemon_name}
              </StrokedText>
              <StrokedText
                fontSize={isXsScreen() ? 10 : 12}
                strokeWidth={4}
                className="text-pk-yellow-1"
              >
                Lv. {pokemon.level}
              </StrokedText>
            </div>

            <PokemonSprite
              pokemon={pokemon}
              style={{
                scale: isXsScreen() ? 1.75 : 2,
                filter: isHovered ? "brightness(1.2)" : "none",
              }}
              className="w-12 h-12 transition-transform duration-200 group-hover:scale-110"
              showShadow={true}
              direction={direction}
              animation={sleepingPokemonIds.has(pokemon.id) ? "Sleep" : "Idle"}
            />
          </div>
        );
      })}
    </div>
  );
}
